import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { toast } from 'vue3-toastify'
import { usePipelineStore } from '@/stores/pipeline.store'
import LeadSourceService from '@/services/lead-source.service'
import LeadStageService from '@/services/lead-stage.service'
import ProjectStageService from '@/services/project-stage.service'
import type { LeadSource } from '@/types/lead-source'
import type { LeadStage } from '@/types/lead-stage'
import type { ProjectStage } from '@/types/project-stage'

export const useSettingsStore = defineStore('settings', () => {
  const pipelineStore = usePipelineStore()

  const projectStageItems = ref<ProjectStage[]>([])
  const isProjectStagesLoading = ref(false)
  const projectStages = computed(() => projectStageItems.value.map(stage => stage.name))

  const projectStageErrorMessage = (error: any, fallback: string): string => {
    return error?.response?.data?.message ?? fallback
  }

  const fetchProjectStages = async (): Promise<void> => {
    isProjectStagesLoading.value = true

    try {
      const { data } = await ProjectStageService.index()
      projectStageItems.value = data.data ?? []
    } catch (error) {
      toast.error(projectStageErrorMessage(error, 'Failed to load project stages.'))
    } finally {
      isProjectStagesLoading.value = false
    }
  }

  const addProjectStage = async (name: string): Promise<void> => {
    try {
      const { data } = await ProjectStageService.store(name)
      projectStageItems.value.push(data.data)
      toast.success('Project stage added.')
    } catch (error) {
      toast.error(projectStageErrorMessage(error, 'Failed to add project stage.'))
    }
  }

  const renameProjectStage = async (id: number, name: string): Promise<void> => {
    try {
      const { data } = await ProjectStageService.update(id, name)
      const index = projectStageItems.value.findIndex(stage => stage.id === id)
      if (index !== -1) projectStageItems.value[index] = data.data
      toast.success('Project stage updated.')
    } catch (error) {
      toast.error(projectStageErrorMessage(error, 'Failed to update project stage.'))
      await fetchProjectStages()
    }
  }

  const deleteProjectStage = async (id: number): Promise<void> => {
    if (projectStages.value.length <= 1) {
      toast.error('At least one project stage is required.')
      return
    }

    try {
      await ProjectStageService.destroy(id)
      projectStageItems.value = projectStageItems.value.filter(stage => stage.id !== id)
      toast.success('Project stage removed.')
    } catch (error) {
      toast.error(projectStageErrorMessage(error, 'Failed to remove project stage.'))
    }
  }

  const leadSourceItems = ref<LeadSource[]>([])
  const isLeadSourcesLoading = ref(false)
  const leadSources = computed(() => leadSourceItems.value.map(source => source.name))

  const leadSourceErrorMessage = (error: any, fallback: string): string => {
    return error?.response?.data?.message ?? fallback
  }

  const fetchLeadSources = async (): Promise<void> => {
    isLeadSourcesLoading.value = true

    try {
      const { data } = await LeadSourceService.index()
      leadSourceItems.value = data.data ?? []
    } catch (error) {
      toast.error(leadSourceErrorMessage(error, 'Failed to load lead sources.'))
    } finally {
      isLeadSourcesLoading.value = false
    }
  }

  const addLeadSource = async (name: string): Promise<void> => {
    try {
      const { data } = await LeadSourceService.store(name)
      leadSourceItems.value.push(data.data)
      toast.success('Lead source added.')
    } catch (error) {
      toast.error(leadSourceErrorMessage(error, 'Failed to add lead source.'))
    }
  }

  const renameLeadSource = async (id: number, name: string): Promise<void> => {
    try {
      const { data } = await LeadSourceService.update(id, name)
      const index = leadSourceItems.value.findIndex(source => source.id === id)
      if (index !== -1) leadSourceItems.value[index] = data.data
      toast.success('Lead source updated.')
    } catch (error) {
      toast.error(leadSourceErrorMessage(error, 'Failed to update lead source.'))
      await fetchLeadSources()
    }
  }

  const deleteLeadSource = async (id: number): Promise<void> => {
    if (leadSources.value.length <= 1) {
      toast.error('At least one lead source is required.')
      return
    }

    try {
      await LeadSourceService.destroy(id)
      leadSourceItems.value = leadSourceItems.value.filter(source => source.id !== id)
      toast.success('Lead source removed.')
    } catch (error) {
      toast.error(leadSourceErrorMessage(error, 'Failed to remove lead source.'))
    }
  }

  const leadStageItems = ref<LeadStage[]>([])
  const isLeadStagesLoading = ref(false)
  const leadStages = computed(() => leadStageItems.value.map(stage => stage.name))

  const leadStageErrorMessage = (error: any, fallback: string): string => {
    return error?.response?.data?.message ?? fallback
  }

  const fetchLeadStages = async (): Promise<void> => {
    isLeadStagesLoading.value = true

    try {
      const { data } = await LeadStageService.index()
      leadStageItems.value = data.data ?? []
    } catch (error) {
      toast.error(leadStageErrorMessage(error, 'Failed to load lead stages.'))
    } finally {
      isLeadStagesLoading.value = false
    }
  }

  const addLeadStage = async (name: string): Promise<void> => {
    try {
      const { data } = await LeadStageService.store(name)
      leadStageItems.value.push(data.data)
      toast.success('Lead stage added.')
    } catch (error) {
      toast.error(leadStageErrorMessage(error, 'Failed to add lead stage.'))
    }
  }

  const renameLeadStage = async (id: number, name: string): Promise<void> => {
    try {
      const { data } = await LeadStageService.update(id, name)
      const index = leadStageItems.value.findIndex(stage => stage.id === id)
      if (index !== -1) leadStageItems.value[index] = data.data
      toast.success('Lead stage updated.')
    } catch (error) {
      toast.error(leadStageErrorMessage(error, 'Failed to update lead stage.'))
      await fetchLeadStages()
    }
  }

  const deleteLeadStage = async (id: number): Promise<void> => {
    if (leadStages.value.length <= 1) {
      toast.error('At least one lead stage is required.')
      return
    }

    try {
      await LeadStageService.destroy(id)
      leadStageItems.value = leadStageItems.value.filter(stage => stage.id !== id)
      toast.success('Lead stage removed.')
    } catch (error) {
      toast.error(leadStageErrorMessage(error, 'Failed to remove lead stage.'))
    }
  }

  const initPipelineStages = async (): Promise<void> => {
    if (pipelineStore.stages.length === 0) {
      await pipelineStore.fetchBoard()
    }
  }

  const addPipelineStage = async (name: string): Promise<void> => {
    await pipelineStore.addStage(name)
  }

  const renamePipelineStage = async (
    id: number,
    name: string
  ): Promise<void> => {
    await pipelineStore.updateStage(id, name)
  }

  const deletePipelineStage = async (id: number): Promise<void> => {
    await pipelineStore.deleteStage(id)
  }

  const movePipelineStageUp = async (index: number): Promise<void> => {
    if (index === 0) return

    const reordered = [...pipelineStore.stages]
    const temp = reordered[index]
    reordered[index] = reordered[index - 1]
    reordered[index - 1] = temp

    const items = reordered.map((s, i) => ({
      id: s.id,
      position: i + 1
    }))

    await pipelineStore.reorderStages(items)
    await pipelineStore.fetchBoard()
  }

  const movePipelineStageDown = async (index: number): Promise<void> => {
    const stages = pipelineStore.stages

    if (index >= stages.length - 1) return

    const reordered = [...stages]
    const temp = reordered[index]
    reordered[index] = reordered[index + 1]
    reordered[index + 1] = temp

    const items = reordered.map((s, i) => ({
      id: s.id,
      position: i + 1
    }))

    await pipelineStore.reorderStages(items)
    await pipelineStore.fetchBoard()
  }

  return {
    pipelineStages: computed(() => pipelineStore.stages),
    isPipelineLoading: computed(() => pipelineStore.loading),
    initPipelineStages,
    addPipelineStage,
    renamePipelineStage,
    deletePipelineStage,
    movePipelineStageUp,
    movePipelineStageDown,
    projectStageItems,
    projectStages,
    isProjectStagesLoading,
    fetchProjectStages,
    addProjectStage,
    renameProjectStage,
    deleteProjectStage,
    leadSourceItems,
    leadSources,
    isLeadSourcesLoading,
    fetchLeadSources,
    addLeadSource,
    renameLeadSource,
    deleteLeadSource,
    leadStageItems,
    leadStages,
    isLeadStagesLoading,
    fetchLeadStages,
    addLeadStage,
    renameLeadStage,
    deleteLeadStage,
  }
})
